import { EllipsisTooltip } from 'App/Map/PanelDetails/OverviewCharts/EllipsisTooltip'
import type {
  CurrentValuesDataInfo,
  TInsectDegreeDaysKey,
} from 'App/Map/PanelDetails/OverviewCharts/OverviewColumns'
import type { CurrentView } from 'App/Map/PanelDetails/OverviewCharts/SectionTable/types'
import { basicSort, pestSorter } from 'App/Map/PanelDetails/OverviewCharts/SectionTable/utils'
import { almondHullSplitColumn } from 'App/Map/PanelDetails/OverviewCharts/_utils/almondHullSplitColumn'
import type { TValueGroupColumns } from 'App/Map/PanelDetails/OverviewCharts/_utils/types'
import type { TColumn } from 'components/SummaryTable/SummaryTable.types'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { unitConverter } from 'utils/unitConverter/unitConverter'

type TDegreeDaysColumn = {
  prefix: string
  summaryKey: 'currentDD' | '7dayDD' | '14dayDD'
  title: string
  insectId: number
  currentView: CurrentView | null
}

const degreeDaysColumn = ({
  prefix,
  summaryKey,
  title,
  insectId,
  currentView,
}: TDegreeDaysColumn): TColumn => ({
  title: <EllipsisTooltip fullText={title} />,
  dataIndex: `${prefix}_insectId`,
  key: `${prefix}_insectId`,
  width: 200,
  render: (_: number, record: CurrentValuesDataInfo) => {
    if (currentView?.pest === 'CROP' && record.type === 'summary' && record.insectId === String(insectId)) {
      const { low = null, high = null } = record[summaryKey] ?? { low: null, high: null }

      if (low === null && high === null) return translate.phrases.templates('-')

      return translate.phrases.templates('{{valueMin}} - {{valueMax}} {{unitSymbol}}', {
        valueMin: unitConverter.insectDegreeDays(low, { decimalPlaces: 1 }).value(),
        valueMax: unitConverter.insectDegreeDays(high, { decimalPlaces: 1 }).value(),
        unitSymbol: unitConverter.insectDegreeDays().suffix(),
      })
    }

    const blockKey = `${prefix}_insectId${insectId}_degreeDaysC`

    if (currentView?.pest === 'BLOCK' && Object.keys(record).includes(blockKey)) {
      return unitConverter
        .insectDegreeDays(Number(record[blockKey as TInsectDegreeDaysKey]), { decimalPlaces: 1 })
        .valueWithSuffix()
    }

    return translate.phrases.templates('-')
  },
  sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) =>
    basicSort(
      pestSorter(a, prefix, 'insectDegreeDays_', currentView),
      pestSorter(b, prefix, 'insectDegreeDays_', currentView),
    ),
})

export const getPestModelColumns = (insectId: number): TValueGroupColumns => ({
  leadingColumn: 'blockName',
  getColumns: ({ currentView }) => [
    {
      title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Crop')} />,
      dataIndex: 'crop',
      key: 'crop',
      width: 150,
      render: (value: string, record: CurrentValuesDataInfo) =>
        currentView?.pest === 'CROP' && record.type === 'summary' ? record.crop || '-' : value || '-',
    },
    {
      title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Biofix Date')} />,
      dataIndex: 'biofix',
      key: 'biofix',
      width: 150,
      render: (value: string, record: CurrentValuesDataInfo) =>
        value
          ? translate.dates.format(moment.tz(value, record.timezone), 'MMM D, YYYY')
          : translate.phrases.templates('-'),
      sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) =>
        a.biofix && b.biofix ? +new Date(a.biofix) - +new Date(b.biofix) : -1,
    },
    degreeDaysColumn({
      prefix: 'insectDegreeDays_sinceBiofixSumMinMedianMax',
      summaryKey: 'currentDD',
      title: translate.phrases.banyanApp('Current'),
      insectId,
      currentView,
    }),
    degreeDaysColumn({
      prefix: 'insectDegreeDays7DaysFromNow_sinceBiofixSumMinMedianMax',
      summaryKey: '7dayDD',
      title: translate.phrases.banyanApp('{{count}} Days From Now', { count: 7 }),
      insectId,
      currentView,
    }),
    degreeDaysColumn({
      prefix: 'insectDegreeDays14DaysFromNow_sinceBiofixSumMinMedianMax',
      summaryKey: '14dayDD',
      title: translate.phrases.banyanApp('{{count}} Days From Now', { count: 14 }),
      insectId,
      currentView,
    }),
    ...(insectId === 6 && currentView?.pest === 'BLOCK' ? [almondHullSplitColumn()] : []),
  ],
})
